import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";

import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";

import BootstrapTable from "react-bootstrap-table-next";
import { max } from "d3-array";

import {
  getTextWidth,
  MAIN_COLORS,
  StandardTooltip,
} from "components/Utils/Utils";
import {
  formatQueryMetric,
  getPredictionBarShape,
} from "components/QueryBuilder/Utils/QueryBuilderUtils";

import Legend from "components/QueryBuilder/Elements/Legend";
import Draggable from "components/QueryBuilder/Elements/Draggable";

import {
  formatDataForSegments,
  formatDataForChart,
} from "components/Utils/ChartsDataUtils";
import {
  Bar,
  BarChart,
  ErrorBar,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import QueryBuilderTooltipPrediction from "components/QueryBuilder/Visualization/tooltips/QueryBuilderTooltipPrediction";

const DragColumn = (props) => {
  const { x, y } = props;
  return (
    <Draggable
      position={{ x, y }}
      radius={20}
      onDrag={({ deltaX }) => props.setWidths(x + deltaX)}
    ></Draggable>
  );
};

const CustomizedLabel = (props) => {
  const { x, y, stroke, value, formatter } = props;
  const increase = (props.width / 100) * props.formatter.percentageIncrease;
  return (
    <text
      x={x + (isNaN(props.width) ? 0 : props.width + increase + 10)}
      y={y}
      dy={14}
      fill={stroke}
      fontSize={12}
      textAnchor="start"
    >
      {formatQueryMetric(
        formatter.metricId,
        value,
        formatter.metrics,
        "segments"
      )}
    </text>
  );
};

// Defines the max width acceptable for the td width
const MAX_VALUE_WIDTH = 180;
const MIN_VALUE_WIDTH = 90;

const QueryBuilderTableWithChart = forwardRef((props, ref) => {
  const { state: globalState } = useContext(QueryBuilderContext);

  // Format Data
  const { segmentsData, segmentsNestedData } = formatDataForSegments(
    props.data,
    props.dimensions,
    globalState.predictions
  );

  const { all_dim_keys } = formatDataForChart(props.dataGroupedByDate);

  // get the max from the value + prediction (in the case that it's available)
  // by some reasone, there are predictions not available for metrics without a value defined (N/A)
  const totalValue = max(
    segmentsData,
    (d) => d.value + (d.prediction ? d.prediction.predMaxMin[0] : 0)
  );

  const itemRef = useRef();
  const [seriesFilter, setSeriesFilter] = useState(null);
  const [widths, setWidths] = useState({});
  let [data, setData] = useState({});
  const [sortTypeState, setSortTypeState] = useState({});
  const [tooltipPayload, setTooltipPayload] = useState(null);

  const [state, setState] = useState({
    metrics: [],
    legendData: [],
    dimLen: props.dimensions.length || 0,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0, // which page  is showing
    sizePerPage: 100, // how many rows per page
  });

  const { metrics, legendData, dimLen } = state;

  useEffect(() => {
    // map the metric and get the longest metric name to get the DOM width that will generated for it
    const longestMetricName = props.metrics
      .map((m) => props.queryMetrics.find((qM) => qM.id === m.toString()))
      .reduce((longestName, currentName) => {
        return currentName?.friendlyName?.length > longestName?.length
          ? currentName.friendlyName
          : longestName;
      }, "");

    // fill metrics
    const metrics = [];
    const metricTextWidth = getTextWidth(longestMetricName);
    metrics.push({
      name: "metric",
      title: "Metric",
      sortDefault: "asc",
      resize: true,
      defaultWidth:
        metricTextWidth > MAX_VALUE_WIDTH
          ? MAX_VALUE_WIDTH
          : metricTextWidth < MIN_VALUE_WIDTH
          ? MIN_VALUE_WIDTH
          : metricTextWidth + 20,
    });

    props.dimensions.forEach((dim) => {
      const dimensionTextWidth = getTextWidth(
        dim.values.reduce(
          (prev, next) => (next.length > prev.length ? next : prev),
          ""
        )
      );

      metrics.push({
        ...dim,
        sortDefault: "asc",
        resize: true,
        defaultWidth:
          dimensionTextWidth > MAX_VALUE_WIDTH
            ? MAX_VALUE_WIDTH
            : dimensionTextWidth < MIN_VALUE_WIDTH
            ? MIN_VALUE_WIDTH
            : dimensionTextWidth,
      });
    });

    // define last dimension
    metrics[metrics.length - 1].sortDefault = "none";
    metrics[metrics.length - 1].isLast = true;
    // add value dimension

    // find all values and convert it to string, returning the one that has the longest string value length
    const longestValueString = props.data
      .map((d) => d.metrics)
      // concatenate the metric array of each element
      .reduce((prev, next) => prev.concat(next), [])
      // remove nulls
      .filter((m) => m.value)
      // get only the value property of the metric
      .map((m) => m.value)
      // convert it to string and return the longest value converted into string
      .reduce(
        (prior, next) =>
          next.toString().length > prior.length ? next.toString() : prior,
        ""
      );

    const valueTextWidth = getTextWidth(longestValueString);
    metrics.push({
      name: "value",
      title: "Value",
      sortDefault: "desc",
      defaultWidth:
        valueTextWidth > MAX_VALUE_WIDTH
          ? MAX_VALUE_WIDTH
          : valueTextWidth < MIN_VALUE_WIDTH
          ? MIN_VALUE_WIDTH
          : valueTextWidth,
    });

    setState({
      ...state,
      metrics,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if change metrics => change sort
  useEffect(() => {
    if (!state.metrics.length) return;
    const obj = {};
    state.metrics.forEach((el) =>
      el.sortDefault ? (obj[el.name] = el.sortDefault) : null
    );
    setSortTypeState(obj);

    const widths = {};
    state.metrics.forEach((el) => (widths[el.name] = el.defaultWidth));
    setWidths(widths);
  }, [state.metrics]);

  // if change sort => change data
  useEffect(() => {
    if (!Object.keys(sortTypeState).length) return;

    const tempColors = {};

    const getColors = (path, d, i) => {
      if (d[1].value !== undefined) {
        d[1].color = MAIN_COLORS[i % 4];
        tempColors[path] = d[1].color;
      } else {
        d[1].forEach((q, i) => getColors(path + ":" + q[0], q, i));
      }
    };
    const data = sortData(segmentsNestedData, 0);
    data.forEach((d) => getColors(d[0], d, 0));
    setData(data);

    // legend data
    const legendData = [
      {
        name: "metrics",
        friendlyName: "Metrics",
        value: [
          {
            name: "select_all",
            friendlyName: "Select all",
            color: "#8a9ca3",
            checked: true,
          },
        ],
      },
    ];

    data.forEach((metric) => {
      legendData[0].value.push({
        name: metric[0],
        friendlyName: metric[0],
        type: "metric",
        color: "#38474d",
        checked: true,
      });

      if (all_dim_keys && all_dim_keys.length) {
        const _metrics = [
          {
            name: "select_all",
            friendlyName: "Select all",
            color: "#8a9ca3",
            checked: true,
          },
        ];

        switch (dimLen) {
          case 1:
            metric[1].forEach((d, i) => {
              if (d[1] /* && d[1].value && d[1].value !== 0*/) {
                const friendlyName = d[1][props.dimensions[0].name + "_name"];

                _metrics.push({
                  name: metric[0] + ":" + friendlyName,
                  friendlyName: friendlyName,
                  color: tempColors[metric[0] + ":" + friendlyName],
                  checked: true,
                });
              }
              return "";
            });
            break;

          case 2:
            metric[1].forEach((D, i) => {
              D[1].forEach((d, i) => {
                if (d[1] /* && d[1].value && d[1].value !== 0*/) {
                  const friendlyName =
                    d[1][props.dimensions[0].name + "_name"] +
                    ":" +
                    d[1][props.dimensions[1].name + "_name"];

                  _metrics.push({
                    name: metric[0] + ":" + friendlyName,
                    friendlyName: friendlyName
                      .split(":")
                      .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
                      .join(" | "),
                    color: tempColors[metric[0] + ":" + friendlyName],
                    checked: true,
                  });
                }
                return "";
              });
            });
            break;

          default:
            break;
        }

        legendData.push({
          name: metric[0],
          friendlyName: metric[0],
          value: _metrics,
        });
      }
    });
    setState({
      ...state,
      legendData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortTypeState]);

  const renderPredictionBar = (data, dataKey, metric) => {
    if (!data.prediction) return;
    const maxDeviation = data.prediction.deviationCalculated[0];
    const { predictedCalculated } = data.prediction;
    const maxDeviationPercentage =
      ((maxDeviation || 1) / predictedCalculated) * 100;
    return (
      <Bar
        dataKey={dataKey}
        fill={data.color}
        // this is a hack to get the LabelList re-rendered
        key={Math.random() * 1}
        isAnimationActive={false}
        yAxisId={1}
        onMouseOver={(d) => {
          // only set the tooltip in the case that it wasn't set yet
          if (
            !tooltipPayload ||
            // or it's a different one
            (tooltipPayload.payload.prediction &&
              tooltipPayload.payload.prediction.predicted !==
                d.payload.prediction.predicted)
          )
            setTooltipPayload(d);
        }}
        onMouseOut={() => setTooltipPayload(null)}
        shape={({ x, y, width, height }) => {
          const BarShape = getPredictionBarShape(
            x,
            y,
            width,
            height,
            data.color.replace("#", "")
          );
          return (
            <g>
              <BarShape
                fillOpacity={1}
                fill={data.color}
                stroke="3 3"
                dataKey={dataKey}
              />
            </g>
          );
        }}
      >
        {globalState.viewUncertainty && (
          <ErrorBar
            dataKey={"prediction.deviationCalculated"}
            width={9}
            strokeWidth={1}
            stroke="gray"
          />
        )}
        <LabelList
          dataKey={"prediction.predictedCalculated"}
          position="end"
          content={
            <CustomizedLabel
              formatter={{
                metricId: metric.id,
                metrics: globalState.queryMetrics,
                percentageIncrease: globalState.viewUncertainty
                  ? maxDeviationPercentage
                  : 0,
              }}
            />
          }
        />
      </Bar>
    );
  };

  const renderTable = (_data, index, total, parentKey) => {
    const metric = metrics[index];
    const isLastDim = index + 1 >= metrics.length - 1;
    if (!isLastDim) {
      if (!_data.length) return;
    } else {
      if (!Object.keys(_data).length) return;
    }

    if (index < metrics.length - 1) {
      const data = _data
        .map((d, i) => {
          return {
            id: Math.random(), // needed for re-rendering BootstrapTable
            key: d[0],
            value: d[1],
          };
        })
        .filter(
          (d) =>
            d.value &&
            //d.value.value !== 0 &&
            (!seriesFilter ||
              (seriesFilter &&
                seriesFilter.findIndex((f) =>
                  isLastDim
                    ? f === (parentKey ? parentKey + ":" + d.key : d.key)
                    : f.indexOf(parentKey ? parentKey + ":" + d.key : d.key) ===
                      0
                ) > -1))
        );

      return (
        <BootstrapTable
          keyField="key"
          data={data}
          wrapperClasses={isLastDim ? "last-table" : ""}
          columns={[
            {
              dataField: "key",
              text: metric.title,

              formatter: (cell, row, rowIndex, colIndex) => {
                if (cell === undefined) return null;
                const cellWidth =
                  (cell.length / 18) * (widths[metric.name] / 150) * 150;

                return (
                  <>
                    {/* if the cell width is greater than width specified in the metric then show ellipsis + tooltip */}
                    {cellWidth > widths[metric.name] ? (
                      <span className="hover-custom-title"></span>
                    ) : (
                      ""
                    )}
                    <span className="span-custom-title" custom-title={cell}>
                      <span>{cell}</span>
                    </span>
                  </>
                );
              },
              style: {
                width: widths[metric.name] + "px",
                maxWidth: widths[metric.name] + "px",
              },
            },
            {
              dataField: "value",
              text: "value",
              ...(isLastDim &&
                _data && {
                  style: {},
                }),
              formatter: (cell, row) => {
                const total =
                  index + 1 === metrics.length - 1
                    ? max(
                        data,
                        (d) =>
                          d.value.value +
                          (d.value.prediction
                            ? d.value.prediction.predMaxMin[0]
                            : 0)
                      )
                    : 0;
                return renderTable(
                  cell,
                  index + 1,
                  total,
                  parentKey ? parentKey + ":" + row.key : row.key,
                  !Array.isArray(cell)
                );
              },
            },
          ]}
        />
      );
    } else {
      const data = [_data];
      const metric = globalState.queryMetrics.find(
        (m) => m.id === _data.metric_id.toString()
      );
      const allBars = [
        <Bar
          isAnimationActive={false}
          dataKey="value"
          // FIXME: This is an workaround to get the label being re-rendered properly.
          key={Math.random() * 1}
          stroke={_data.color}
          fill={_data.color}
        >
          {!_data.prediction && (
            <LabelList
              dataKey={"value"}
              position="right"
              content={
                <CustomizedLabel
                  formatter={{
                    metricId: metric.id,
                    metrics: globalState.queryMetrics,
                    percentageIncrease: 0,
                  }}
                />
              }
            />
          )}
        </Bar>,
        renderPredictionBar(_data, "prediction.predictedCalculated", metric),
      ];

      // defines the max bar width according the longest value.toString lengh
      // transform it to percentage and decrease it in comparison to the max acceptable
      // length which is MAX_VALUE_WIDTH
      const maxBarWidth =
        (metrics[metrics.length - 1].defaultWidth / MAX_VALUE_WIDTH) * 90;

      return (
        <div
          style={{
            width: "100%",
          }}
        >
          <ResponsiveContainer height={30} width={maxBarWidth + "%"}>
            <BarChart layout="vertical" data={data} barCategoryGap={0}>
              <Tooltip
                cursor={{ fill: "#eee", opacity: 0.5 }}
                content={
                  <StandardTooltip
                    render={() => {
                      if (tooltipPayload) {
                        return (
                          <div className="standard-tooltip">
                            <main className="standard-tooltip__module">
                              <div className="standard-tooltip__item">
                                <span
                                  className="standard-tooltip__icon"
                                  style={{
                                    background: tooltipPayload.color,
                                  }}
                                ></span>
                                <span className="standard-tooltip__key text-capitalize">
                                  {tooltipPayload.dimensionFriendlyName ||
                                    tooltipPayload.metric}
                                </span>
                                <br />
                              </div>
                            </main>
                            <QueryBuilderTooltipPrediction
                              tooltipPayload={tooltipPayload}
                              metrics={globalState.queryMetrics}
                            />
                          </div>
                        );
                      }
                    }}
                  />
                }
              />
              <YAxis dataKey="value" yAxisId={0} type="category" hide />
              <YAxis dataKey="value" yAxisId={1} type="category" hide />
              <XAxis type="number" domain={[0, total || 0]} hide />

              {allBars}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
  };

  const onChangeSelection = (selection) => {
    const checkedMetrics = selection.metrics
      .slice(1)
      .filter((d) => d.checked)
      .map((d) => d.name);
    const _seriesFilter = checkedMetrics.slice(0);
    checkedMetrics.forEach(
      (m) =>
        selection[m] &&
        selection[m]
          .slice(1)
          .forEach((d) => d.checked && _seriesFilter.push(d.name))
    );
    setSeriesFilter(_seriesFilter);
  };

  const sortData = (_data, dimIndex) => {
    const metric = metrics[dimIndex];
    const data = !Array.isArray(_data) ? [] : _data;
    const sort = (a, b, order) => {
      if (a.length !== 0 || b.length !== 0) return true;
      if (order === "desc") {
        return b[0].toLowerCase().localeCompare(a[0].toLowerCase());
      } else if (order === "asc") {
        return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
      } else {
        return true;
      }
    };

    const sortLast = (a, b, order) => {
      if (order === "desc") {
        return a[1].value > b[1].value ? -1 : a[1].value < b[1].value ? 1 : 0;
      } else if (order === "asc") {
        return a[1].value > b[1].value ? 1 : a[1].value < b[1].value ? -1 : 0;
      } else {
        return true;
      }
    };

    if (metric.isLast && sortTypeState[metric.name] !== "none") {
      return data
        .slice(0)
        .sort((a, b) => sort(a, b, sortTypeState[metric.name]));
    } else if (metric.isLast && sortTypeState[metric.name] === "none") {
      const valueMetric = metrics[dimIndex + 1];
      return data
        .slice(0)
        .sort((a, b) => sortLast(a, b, sortTypeState[valueMetric.name]));
    } else {
      return data
        .slice(0)
        .sort((a, b) => sort(a, b, sortTypeState[metric.name]))
        .map((d) => [d[0], sortData(d[1], dimIndex + 1)]);
    }
  };

  // get the data for the table slicing the data according the pagination
  const slicedData =
    // only slice the data if the data is an array
    !Array.isArray(data) ||
    // or if the data contains the information regarding the number or fows
    !(data.length > 0 && data[0] && Array.isArray(data[0][1]))
      ? []
      : data.reduce(
          (prev, current) => [
            ...prev,
            [
              current[0],
              current[1].slice(
                paginationState.page * paginationState.sizePerPage,
                paginationState.page * paginationState.sizePerPage +
                  paginationState.sizePerPage
              ),
            ],
          ],
          []
        );

  const totalPages = !(data.length > 0 && data[0] && Array.isArray(data[0][1]))
    ? 0
    : Math.ceil(data[0][1].length / paginationState.sizePerPage);

  const paginationComp = (
    <div className="row react-bootstrap-table-pagination">
      <div className="col-md-6 col-xs-6 col-sm-6 col-lg-6"></div>
      <div className="react-bootstrap-table-pagination-list col-md-6 col-xs-6 col-sm-6 col-lg-6">
        <ul className="pagination react-bootstrap-table-page-btns-ul">
          {totalPages > 1 &&
            [...Array(totalPages).keys()].map((page) => (
              <li
                key={page}
                className={
                  (page + 1 === paginationState.page + 1 ? "active" : "") +
                  " page-item"
                }
                title="1"
                onClick={() =>
                  setPaginationState({ ...paginationState, page: page })
                }
              >
                <button className="page-link">{page + 1}</button>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="row animated fadeIn">
      <div className="col-10">
        <div className="segmentation-table query-view-border">
          <div className="thead d-flex" ref={itemRef}>
            {metrics.map((metric, i) => (
              <div
                key={"head_" + metric.name}
                className="th"
                style={{
                  width: widths[metric.name] + (i > 0 ? 1 : 0) + "px",
                  flexGrow: i === metrics.length - 1 ? 1 : 0,
                }}
              >
                <div className="table-title">{metric.title}</div>
                {metric.resize ? (
                  <DragColumn
                    x={widths[metric.name]}
                    y={0}
                    setWidths={(width) => {
                      const fullWidthWithoutWidth =
                        Object.keys(widths).reduce(
                          (acc, cur) =>
                            (acc += cur === metric.name ? 0 : widths[cur] || 0),
                          0
                        ) + 110;
                      const fullWidth = itemRef.current.clientWidth;
                      return setWidths({
                        ...widths,
                        [metric.name]:
                          width < 110
                            ? 110
                            : fullWidthWithoutWidth + width > fullWidth
                            ? fullWidth - fullWidthWithoutWidth
                            : width,
                      });
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
          {/* <div className="sub-thead">10 INSIGHTS UNCOVERED</div> */}
          {metrics.length
            ? renderTable(totalPages > 0 ? slicedData : data, 0, totalValue, "")
            : null}
        </div>
        {paginationComp}
      </div>
      <div className="col-2">
        <Legend data={legendData} onChangeSelection={onChangeSelection} />
      </div>
    </div>
  );
});

export default React.memo(QueryBuilderTableWithChart);
